/**
 * Endpoints de **Gestión → Inventario** (`/api/gestion/inventario/*`).
 *
 * Las tres pantallas (listado, entradas y salidas) comparten los mismos datos de
 * producto, así que también comparten la clave de caché: al registrar o anular un
 * movimiento se refrescan todas.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  CompraPendiente,
  EntradaInventario,
  FiltrosInventario,
  InventarioRespuesta,
  MovimientosInventarioRespuesta,
  SalidaInventario,
} from './gestion.types';

const CLAVE = 'gestion';

export const inventarioKeys = {
  listado: (search: string, tipo: string) => [CLAVE, 'inventario', 'listado', search, tipo] as const,
  entradas: (filtros: FiltrosInventario) => [CLAVE, 'inventario', 'entradas', filtros] as const,
  salidas: (filtros: FiltrosInventario) => [CLAVE, 'inventario', 'salidas', filtros] as const,
  compras: (productoId: number | null) =>
    [CLAVE, 'inventario', 'compras-pendientes', productoId] as const,
};

/** Cadena de consulta de un objeto de filtros. */
function cadenaDe<T extends object>(filtros: T): string {
  const parametros = new URLSearchParams();

  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor !== undefined && valor !== null && valor !== '') {
      parametros.set(clave, String(valor));
    }
  }

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const inventarioApi = {
  listado: (search: string, tipo: string, signal?: AbortSignal): Promise<InventarioRespuesta> =>
    apiClient.get<InventarioRespuesta>(
      `/api/gestion/inventario${cadenaDe({ search, tipo })}`,
      { signal },
    ),

  entradas: (
    filtros: FiltrosInventario,
    signal?: AbortSignal,
  ): Promise<MovimientosInventarioRespuesta> =>
    apiClient.get<MovimientosInventarioRespuesta>(
      `/api/gestion/inventario/entradas${cadenaDe(filtros)}`,
      { signal },
    ),

  salidas: (
    filtros: FiltrosInventario,
    signal?: AbortSignal,
  ): Promise<MovimientosInventarioRespuesta> =>
    apiClient.get<MovimientosInventarioRespuesta>(
      `/api/gestion/inventario/salidas${cadenaDe(filtros)}`,
      { signal },
    ),

  comprasPendientes: (productoId: number, signal?: AbortSignal): Promise<{ compras: CompraPendiente[] }> =>
    apiClient.get<{ compras: CompraPendiente[] }>(
      `/api/gestion/inventario/compras-pendientes${cadenaDe({ producto_id: productoId })}`,
      { signal },
    ),

  registrarEntrada: (datos: EntradaInventario): Promise<{ entrada_id: number; mensaje: string }> =>
    apiClient.post<{ entrada_id: number; mensaje: string }>('/api/gestion/inventario/entradas', {
      body: datos,
    }),

  registrarSalida: (datos: SalidaInventario): Promise<{ salida_id: number; mensaje: string }> =>
    apiClient.post<{ salida_id: number; mensaje: string }>('/api/gestion/inventario/salidas', {
      body: datos,
    }),

  anularEntrada: (detalleId: number): Promise<{ mensaje: string }> =>
    apiClient.delete<{ mensaje: string }>(`/api/gestion/inventario/entradas/${detalleId}`),

  anularSalida: (detalleId: number): Promise<{ mensaje: string }> =>
    apiClient.delete<{ mensaje: string }>(`/api/gestion/inventario/salidas/${detalleId}`),
};

/** Listado de productos con su stock. */
export function useInventario(search = '', tipo = '') {
  return useQuery({
    queryKey: inventarioKeys.listado(search, tipo),
    queryFn: ({ signal }) => inventarioApi.listado(search, tipo, signal),
    staleTime: 30_000,
  });
}

/** Entradas registradas. */
export function useInventarioEntradas(filtros: FiltrosInventario = {}, habilitada = true) {
  return useQuery({
    queryKey: inventarioKeys.entradas(filtros),
    queryFn: ({ signal }) => inventarioApi.entradas(filtros, signal),
    enabled: habilitada,
    staleTime: 15_000,
  });
}

/** Salidas registradas. */
export function useInventarioSalidas(filtros: FiltrosInventario = {}, habilitada = true) {
  return useQuery({
    queryKey: inventarioKeys.salidas(filtros),
    queryFn: ({ signal }) => inventarioApi.salidas(filtros, signal),
    enabled: habilitada,
    staleTime: 15_000,
  });
}

/** Compras del producto que todavía no se han almacenado. */
export function useComprasPendientes(productoId: number | null) {
  return useQuery({
    queryKey: inventarioKeys.compras(productoId),
    queryFn: ({ signal }) => inventarioApi.comprasPendientes(productoId as number, signal),
    enabled: Boolean(productoId),
    staleTime: 15_000,
  });
}

/** Refresca todo el inventario (listado y movimientos). */
function refrescarInventario(cliente: ReturnType<typeof useQueryClient>) {
  void cliente.invalidateQueries({ queryKey: [CLAVE, 'inventario'] });
}

/** Registra una entrada. */
export function useRegistrarEntrada() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: inventarioApi.registrarEntrada,
    onSuccess: () => refrescarInventario(cliente),
  });
}

/** Registra una salida. */
export function useRegistrarSalida() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: inventarioApi.registrarSalida,
    onSuccess: () => refrescarInventario(cliente),
  });
}

/** Anula una entrada (borrado lógico y devolución del stock). */
export function useAnularEntrada() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: inventarioApi.anularEntrada,
    onSuccess: () => refrescarInventario(cliente),
  });
}

/** Anula una salida (borrado lógico y devolución del stock). */
export function useAnularSalida() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: inventarioApi.anularSalida,
    onSuccess: () => refrescarInventario(cliente),
  });
}
